import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * Address Input with Yandex Geosuggest autocomplete
 * All API calls go through backend for security (API keys hidden)
 */
function AddressAutocomplete({ 
  value, 
  onChange, 
  onAddressSelect,
  cityCode = 'almaty',
  placeholder = 'Начните вводить адрес...',
  className = ''
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync external value
  useEffect(() => {
    if (value !== query) {
      setQuery(value || '');
    }
  }, [value]);

  // Fetch suggestions through backend (API key hidden on server)
  const fetchSuggestions = async (text) => {
    if (text.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/delivery/suggest`, {
        params: {
          query: text,
          city_code: cityCode
        }
      });
      
      const formattedSuggestions = (response.data?.suggestions || []).map(item => ({
        title: item.title || '',
        subtitle: item.subtitle || '',
        full_address: item.full_address || `${item.title || ''}, ${item.subtitle || ''}`.trim().replace(/,\s*$/, ''),
        uri: item.uri || ''
      }));
      
      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error('Geosuggest error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange && onChange(text);

    // Debounce API calls (300ms)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(text);
    }, 300);
  };

  const handleSelect = async (suggestion) => {
    const fullAddress = suggestion.full_address || suggestion.title;
    setQuery(fullAddress);
    setShowDropdown(false);
    setSuggestions([]);
    
    onChange && onChange(fullAddress);
    
    // Geocode through backend (API key hidden)
    if (onAddressSelect) {
      try {
        const response = await axios.post(
          `${API}/delivery/geocode?address=${encodeURIComponent(fullAddress)}`
        );
        onAddressSelect({
          address: fullAddress,
          title: suggestion.title,
          subtitle: suggestion.subtitle,
          district: response.data?.district || null,
          coordinates: response.data?.coordinates || null,
          city: response.data?.city || null
        });
      } catch (error) {
        // If geocoding fails, still provide the address
        console.error('Geocoding error:', error);
        onAddressSelect({
          address: fullAddress,
          title: suggestion.title,
          subtitle: suggestion.subtitle,
          district: null,
          coordinates: null
        });
      }
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-9"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors"
              onClick={() => handleSelect(suggestion)}
            >
              <div className="font-medium text-sm text-gray-900">
                {suggestion.title}
              </div>
              {suggestion.subtitle && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {suggestion.subtitle}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && !loading && query.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
          Адрес не найден. Попробуйте уточнить.
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
